import { fetchClient } from './client';

export interface SessionProperties {
  /** The timeout in seconds before the session expires due to inactivity */
  timeout: number;
  /** The maximum duration in seconds the session can last */
  maximumTimeout: number;
  /** Whether clipboard transfer into the session is allowed */
  clipboardTransferIn: boolean;
  /** Whether clipboard transfer out of the session is allowed */
  clipboardTransferOut: boolean;
  /** The timeout in seconds before the session expires if an error occurs */
  errorTimeout?: number;
  /** Extra arguments passed to the browser */
  extraBrowserArgs?: string[];
  /** Extra data associated with the session */
  extraData?: Record<string, string>;
  /** List of plugins enabled for the session */
  plugins?: string[];
  /** The process ID of the browser */
  processID?: string;
  /** The proxy server used by the session */
  proxy?: string;
  /** The screen resolution of the session */
  screenResolution?: string;
  /** The process ID of the script */
  scriptProcessID?: string;
  /** The location of the seastar service */
  seastarLocation?: string;
  /** Whether the session is read-only */
  sessionReadOnly: boolean;
  /** The user agent string used by the browser */
  userAgent?: string;
  /** The video compression level */
  videoCompressionLevel?: string;
  /** The video quality level */
  videoQualityLevel?: string;
}

export interface Session {
  /** The status of the session */
  status: string;
  /** List of window IDs associated with the session */
  windows: string[];
  /** The expiration time of the session as a Unix timestamp (seconds) */
  expiration: number;
  /** The ID of the session */
  id: string;
  /** The properties of the session */
  properties: SessionProperties;
  /** The URL opened in the session */
  url?: string;
}

export interface InputEvent {
  type: string;
  keyCode?: number;
  mouseX?: number;
  mouseY?: number;
  mouseButton?: 'left' | 'middle' | 'right';
  deltaY?: number;
  modifiers?: string[];
  keyboardLayout?: string;
}

export const sendStatistics = async (
  sessionId: string,
  stats: Record<string, unknown>,
): Promise<void> => {
  try {
    await fetchClient(`/bi/v1/sessions/statistics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, ...stats }),
    });
  } catch (err) {
    console.warn('Failed to send statistics', err);
  }
};

export const resolveSession = async (sessionId: string): Promise<Session> => {
  const response = await fetchClient(`/bi/v1/sessions/${sessionId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return response.json();
};

export const endSession = async (sessionId: string): Promise<void> => {
  await fetchClient(`/bi/v1/sessions/${sessionId}`, {
    method: 'DELETE',
  });
};

export const sendInputEvents = async (
  sessionId: string,
  windowId: string,
  events: InputEvent[],
): Promise<void> => {
  await fetchClient(`/bi/v1/sessions/${sessionId}/input/${windowId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      window_id: windowId,
      events,
    }),
  });
};

export const getClipboard = async (
  sessionId: string,
  type: string = 'text',
): Promise<string | Blob> => {
  const response = await fetchClient(
    `/bi/v1/sessions/${sessionId}/clipboard?type=${encodeURIComponent(type)}`,
    {
      method: 'GET',
    },
  );

  const contentType = response.headers.get('content-type');
  if (contentType === 'image/jpeg') {
    return response.blob();
  }

  return response.text();
};

export const setClipboard = async (
  sessionId: string,
  data: string,
): Promise<void> => {
  await fetchClient(`/bi/v1/sessions/${sessionId}/clipboard`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
    },
    body: data,
  });
};
